import React from "react";
import { ICheckParkingRight } from "../ParkingRightsGrid";
import Data from "../../DataRowCol/DataRowCol";
import CustomCheckbox from "../../../../ui/CustomCheckbox/CustomCheckbox";
import { Box } from "@material-ui/core";
import { DirectionsCarOutlined, ErrorOutline } from "@material-ui/icons";
import Skeleton from "@material-ui/lab/Skeleton";
import styled from "styled-components";
import { variables } from "../../../../theme/variables";
import { isUndefined } from "underscore";
import {
  selectBusinessSchedulingAdditions,
  selectUseBulkVehicleAssignments,
  useGetOccupiedParkingRightsCount,
} from "../../../../containers/FleetManager/MyProducts/ParkingRightsDataTable/common";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../store";
import { ParkingProductType } from "../../../../models/ParkingProductModel";
import doorIcon from "../../../../assets/images/door.svg";
import QpTooltip from "../../../QpTooltip";

const { colors } = variables;

const StyledErrorOutline = styled(ErrorOutline)`
  fill: ${colors.red.hex};
  width: 20px;
`;

interface IGridBodyProps {
  loading: boolean;
  state: ICheckParkingRight[];
  onChecked?: (event: React.ChangeEvent<HTMLInputElement>, pr: ICheckParkingRight) => void;
  disableEditing?: boolean;
  renderRowExpansion?: (pr: ICheckParkingRight) => React.ReactNode;
}

const GridBody = (props: IGridBodyProps) => {
  const getTotalOccupiedParkingRights = useGetOccupiedParkingRightsCount();
  const { t } = useTranslation(["vehicleAssignment"]);
  const useBulkVehicleAssignments = useAppSelector(selectUseBulkVehicleAssignments);
  const businessSchedulingAdditions = useAppSelector(selectBusinessSchedulingAdditions);
  const capacityIsExceeded = (pr: ICheckParkingRight) => {
    if (isUndefined(pr.right)) {
      return false;
    }

    if (pr.right!.productType == ParkingProductType.PedestrianAccess) {
      return pr.right!.totalDelegatedPedestrianRights > pr.right!.totalPedestrianRights;
    }

    return getTotalOccupiedParkingRights(pr.right!) > pr.right!.totalParkingRights!;
  };
  const hasUnlimitedEntryRights = (pr: ICheckParkingRight) => pr.right?.hasUnlimitedEntryRights && useBulkVehicleAssignments;

  const getTotalAvailableRights = (pr: ICheckParkingRight) => {
    if (!pr.right) {
      return;
    }

    let occupiedRights = getTotalOccupiedParkingRights(pr.right);
    let totalRights = pr.right?.totalParkingRights;

    if (pr.right.productType == ParkingProductType.PedestrianAccess) {
      occupiedRights = pr.right.totalDelegatedPedestrianRights;
      totalRights = pr.right.totalPedestrianRights;
    }

    if (hasUnlimitedEntryRights(pr)) {
      totalRights = t("unlimited");
    }

    return `${occupiedRights} / ${totalRights}`;
  };

  const getTotalAvailableRightsIcon = (productType: ParkingProductType | undefined) => {
    if (productType == ParkingProductType.PedestrianAccess) {
      return <embed src={doorIcon} width={16} height={16} />;
    }

    return <DirectionsCarOutlined classes={{ root: "grayish-blue large-important" }} />;
  };

  if (!props.loading && props.state) {
    return (
      <>
        {props.state.map((pr: ICheckParkingRight, index: number) => (
          <React.Fragment key={`${pr.right?.pmcName}_${index}`}>
            <Data.Row disabled={pr.disabled}>
              <Data.Col xs={8}>
                {!props.disableEditing && (
                  <CustomCheckbox
                    label={`${pr.right?.location} - ${pr.right?.pmcName}`}
                    checkboxProps={{
                      id: `${pr.right?.pmcId}-${pr.right?.contractType}`,
                      name: `rights[${index}]`,
                      value: `${pr.right?.pmcId}-${pr.right?.contractType}`,
                      checked: pr.checked,
                      onChange: (event) => props.onChecked?.(event, pr),
                      disabled: pr.disabled,
                    }}
                  />
                )}
                {props.disableEditing && (
                  <span>{`${pr.right?.location} - ${pr.right?.pmcName}`}</span>
                )}
              </Data.Col>
              <Data.Col xs={4}>
                <Box component="div" className="d-flex row-wrap justify-right align-center">
                  <Box component="span" className="available-count">
                    {capacityIsExceeded(pr) && !hasUnlimitedEntryRights(pr) && <StyledErrorOutline />}
                    {getTotalAvailableRights(pr)}
                    {businessSchedulingAdditions && pr.hasFutureCapacities && (
                      <QpTooltip
                        text={t("parkingProducts:futureCapacitiesTooltip")}
                        iconName="triangle-exclamation"
                        iconColor="var(--primaryOrange)"
                        iconFolder="fa-solid"
                        iconSize={16}
                        style={{ padding: '4px' }}
                      />
                    )}
                  </Box>
                  &nbsp;
                  {getTotalAvailableRightsIcon(pr.right?.productType)}
                </Box>
              </Data.Col>
              {pr.checked && props.renderRowExpansion && (
                <Data.Col xs={12}>
                  {props.renderRowExpansion(pr)}
                </Data.Col>
              )}
            </Data.Row>
          </React.Fragment>
        ))}
      </>
    );
  } else {
    return (
      <>
        {[1, 2, 3, 4].map((skeleton) => (
          <SkeletonGrid key={skeleton} />
        ))}
      </>
    );
  }
};

const SkeletonGrid = () => (
  <Data.Row disabled={false}>
    <Data.Col xs={8}>
      <Skeleton animation="wave" height={25} />
    </Data.Col>
    <Data.Col xs={2}>
      <></>
    </Data.Col>
    <Data.Col xs={2}>
      <Skeleton animation="wave" height={25} />
    </Data.Col>
  </Data.Row>
);

export default GridBody;
